import {
  Divider,
  FormField,
  Heading,
  InlineNotification,
  InputWidth,
  ReadOnlyField,
  RecordContent,
  RecordFooter,
  useTitleChange,
} from '@msaf/core-react'
import { useEffect, useState } from 'react'
import {
  WellEndPointFields,
  WellGeothermalFieldsView,
  WellGeothermalPressureFields,
  WellGeothermalTemperatureFields,
  WellLogOrSurveyFields,
} from '../../../../../types/form-types'
import NotesWrapperView from '../../../../components/well-forms/notes/notes-wrapper-view'
import WellEndPointView from '../../../../components/well-forms/well-geothermal/well-end-point-view'
import WellGeothermalPressureView from '../../../../components/well-forms/well-geothermal/well-geothermal-pressure-reading-view'
import WellGeothermalTemperatureView from '../../../../components/well-forms/well-geothermal/well-geothermal-temperature-reading-view'
import WellLogOrSurveyView from '../../../../components/well-forms/well-geothermal/well-log-or-survey-view'
import WellViewButtons from '../../../../components/wells/well-view-buttons'
import { useGetWellData } from '../../../../hooks/use-api'
import { useWellId } from '../../../../hooks/use-well-id'
import { getRandomID } from '../../../../services/form-utils'

export default function ViewWellGeothermal() {
  useTitleChange('View Well | Geothermal')
  const id = useWellId()
  const url = `wells/${id}/geothermal/view`
  const [fields, setFields] = useState<Partial<WellGeothermalFieldsView>>({})
  const { isLoading, isSuccess, data } = useGetWellData(url)

  useEffect(() => {
    if (isSuccess && data.state) {
      setFields(data.state)
    }
  }, [isSuccess, data])

  return (
    <>
      <RecordContent>
        <Heading isSkeleton={isLoading} level={2}>
          Geothermal
        </Heading>
        {!isLoading && !fields.isGeothermalWell && (
          <InlineNotification isDismissable={false}>This well is not a Geothermal well</InlineNotification>
        )}
        <Heading isSkeleton={isLoading} level={3}>
          Details
        </Heading>
        <FormField isSkeleton={isLoading} labelId='geothermalId' label='Geothermal ID'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='geothermalId' value={`${fields.geothermalId ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='geothermalField' label='Geothermal field'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='geothermalField'
            value={`${fields.geothermalField ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='trueVerticalDepth' label='True vertical depth (m)'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='trueVerticalDepth'
            value={`${fields.trueVerticalDepth ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='wellheadPressure' label='Wellhead pressure (kPa)'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='wellheadPressure'
            value={`${fields.wellheadPressure ?? ''}`}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='wellCompletionSignoffStandard'
          label='Well completion signoff standard'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='wellCompletionSignoffStandard'
            value={`${fields.wellCompletionSignoffStandard ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='wellCompletionSignoffBy' label='Well completion signoff by'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='wellCompletionSignoffBy'
            value={`${fields.wellCompletionSignoffBy ?? ''}`}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='crossSectionalConstructionPlan'
          label='Cross sectional construction plan'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='crossSectionalConstructionPlan'
            value={`${fields.crossSectionalConstructionPlan ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='sitePlan' label='Site plan'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='sitePlan' value={`${fields.sitePlan ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='masterValve' label='Master valve'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='masterValve' value={`${fields.masterValve ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='cellar' label='Cellar'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='cellar' value={`${fields.cellar ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='downholeFormationConditions' label='Downhole formation conditions'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='downholeFormationConditions'
            value={`${fields.downholeFormationConditions ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='cementType' label='Cement type'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='cementType' value={`${fields.cementType ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='holeDirectionAzimuth' label='Hole direction (degrees)'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='holeDirectionAzimuth'
            value={`${fields.holeDirectionAzimuth ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasUnrecoveredFish' label='Unrecovered fish details'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasUnrecoveredFish'
            value={fields?.hasUnrecoveredFish}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='unrecoveredFishDetails' label='Unrecovered fish details'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='unrecoveredFishDetails'
            value={`${fields.unrecoveredFishDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasExternalCorrosionLeakage' label='Annular flow outside casings'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasExternalCorrosionLeakage'
            value={fields?.hasExternalCorrosionLeakage}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='annularFlowOutsideCasingsDetails'
          label='Annular flow outside casings details'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='annularFlowOutsideCasingsDetails'
            value={`${fields.annularFlowOutsideCasingsDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasChemicalDepositionScale' label='Chemical deposition scale'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasChemicalDepositionScale'
            value={fields?.hasChemicalDepositionScale}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='chemicalDepositionScaleDetails'
          label='Chemical deposition scale details'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='chemicalDepositionScaleDetails'
            value={`${fields.chemicalDepositionScaleDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='enthalpy' label='Enthalpy (kj/kg)'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='enthalpy'
            value={`${fields.enthalpy ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='waterQuality' label='Water quality'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='waterQuality' value={`${fields.waterQuality ?? ''}`} />
        </FormField>
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Well end points
        </Heading>
        {(!fields?.wellEndPoints || (Array.isArray(fields.wellEndPoints) && !fields.wellEndPoints.length)) && (
          <p>This well record has no end points</p>
        )}
        {Array.isArray(fields.wellEndPoints) &&
          fields?.wellEndPoints.map((wellEndPoint: Partial<WellEndPointFields>, index: any) => (
            <WellEndPointView
              key={wellEndPoint.id || getRandomID()}
              fields={wellEndPoint}
              index={index}
              isLoading={isLoading}
            />
          ))}
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Well logs and surveys
        </Heading>
        {(!fields?.wellLogOrSurveys || (Array.isArray(fields.wellLogOrSurveys) && !fields.wellLogOrSurveys.length)) && (
          <p>This well record has no well logs or surveys</p>
        )}
        {Array.isArray(fields.wellLogOrSurveys) &&
          fields?.wellLogOrSurveys.map((wellLogOrSurvey: Partial<WellLogOrSurveyFields>, index: any) => (
            <WellLogOrSurveyView
              key={wellLogOrSurvey.id || getRandomID()}
              fields={wellLogOrSurvey}
              index={index}
              isLoading={isLoading}
            />
          ))}
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Pressure readings
        </Heading>
        {(!fields?.wellGeothermalPressureReadings ||
          (Array.isArray(fields.wellGeothermalPressureReadings) && !fields.wellGeothermalPressureReadings.length)) && (
          <p>This well record has no pressure readings</p>
        )}
        {Array.isArray(fields.wellGeothermalPressureReadings) &&
          fields?.wellGeothermalPressureReadings.map(
            (wellGeothermalPressureReading: Partial<WellGeothermalPressureFields>, index: any) => (
              <WellGeothermalPressureView
                key={wellGeothermalPressureReading.id || getRandomID()}
                fields={wellGeothermalPressureReading}
                index={index}
                isLoading={isLoading}
              />
            ),
          )}
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Temperature readings
        </Heading>
        {(!fields?.wellGeothermalTemperatureReadings ||
          (Array.isArray(fields.wellGeothermalTemperatureReadings) &&
            !fields.wellGeothermalTemperatureReadings.length)) && <p>This well record has no temperature readings</p>}
        {Array.isArray(fields.wellGeothermalTemperatureReadings) &&
          fields?.wellGeothermalTemperatureReadings.map(
            (wellGeothermalTemperatureReading: Partial<WellGeothermalTemperatureFields>, index: any) => (
              <WellGeothermalTemperatureView
                key={wellGeothermalTemperatureReading.id || getRandomID()}
                fields={wellGeothermalTemperatureReading}
                index={index}
                isLoading={isLoading}
              />
            ),
          )}
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Condition of well
        </Heading>
        <FormField isSkeleton={isLoading} labelId='hasExternalCorrosionLeakage' label='External corrosion leakage'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasExternalCorrosionLeakage'
            value={fields?.hasExternalCorrosionLeakage}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='externalCorrosionLeakageDetails'
          label='External corrosion leakage details'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='externalCorrosionLeakageDetails'
            value={`${fields.externalCorrosionLeakageDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasWellheadCorrosionLeakage' label='Wellhead corrosion leakage'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasWellheadCorrosionLeakage'
            value={fields?.hasWellheadCorrosionLeakage}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='wellheadCorrosionLeakageDetails'
          label='Wellhead corrosion leakage details'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='wellheadCorrosionLeakageDetails'
            value={`${fields.wellheadCorrosionLeakageDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasBrokenOrPerforatedCasing' label='Broken or perforated casing'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasBrokenOrPerforatedCasing'
            value={fields?.hasBrokenOrPerforatedCasing}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='brokenOrPerforatedCasingDetails'
          label='Broken or perforated casing details'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='brokenOrPerforatedCasingDetails'
            value={`${fields.brokenOrPerforatedCasingDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasFailedCasingConnections' label='Failed casing connections'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasFailedCasingConnections'
            value={fields?.hasFailedCasingConnections}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='failedCasingConnectionsDetails'
          label='Failed casing connections details'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='failedCasingConnectionsDetails'
            value={`${fields.failedCasingConnectionsDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasLeaksIntoOutOfCasing' label='Leaks from casing'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasLeaksIntoOutOfCasing'
            value={fields?.hasLeaksIntoOutOfCasing}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='leaksIntoOutOfCasingDetails' label='Leaks from casing details'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='leaksIntoOutOfCasingDetails'
            value={`${fields.leaksIntoOutOfCasingDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasBuckledDistortedCasing' label='Buckled distorted casing'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasBuckledDistortedCasing'
            value={fields?.hasBuckledDistortedCasing}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='buckledDistortedCasingDetails'
          label='Buckled distorted casing details'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='buckledDistortedCasingDetails'
            value={`${fields.buckledDistortedCasingDetails ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='hasCorrodedCasing' label='Corroded casing'>
          <ReadOnlyField
            fieldSize={InputWidth.large}
            isSkeleton={isLoading}
            labelledBy='hasCorrodedCasing'
            value={fields?.hasCorrodedCasing}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='corrodedCasingDetails' label='Corroded casing details'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='corrodedCasingDetails'
            value={`${fields.corrodedCasingDetails ?? ''}`}
          />
        </FormField>
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Abandonment
        </Heading>
        <FormField isSkeleton={isLoading} labelId='wellAbandonmentStandard' label='Well abandonment standard'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='wellAbandonmentStandard'
            value={`${fields.wellAbandonmentStandard ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='wellAbandonmentDetails' label='Well abandonment details'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='wellAbandonmentDetails'
            value={`${fields.wellAbandonmentDetails ?? ''}`}
          />
        </FormField>
        <Divider />
        <Heading isSkeleton={isLoading} level={3}>
          Document references
        </Heading>
        <FormField isSkeleton={isLoading} labelId='wellSpecifications' label='Well specifications'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='wellSpecifications'
            value={`${fields.wellSpecifications ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='deviatedWellPath' label='Deviated well path'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='deviatedWellPath'
            value={`${fields.deviatedWellPath ?? ''}`}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='branchedWellDetails' label='Branched well details'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='branchedWellDetails'
            value={`${fields.branchedWellDetails ?? ''}`}
          />
        </FormField>
      </RecordContent>
      <RecordFooter
        notes={
          Array.isArray(fields.notes) &&
          !!fields.notes.length && <NotesWrapperView notes={fields.notes} isLoading={isLoading} />
        }
        areActionsFixed={true}
      >
        {fields.isGeothermalWell && <WellViewButtons wellId={id} />}
      </RecordFooter>
    </>
  )
}
